/**
 * IntercomLauncherToggle
 *
 * Headless component that shows/hides the default Intercom launcher bubble.
 * When mounted (canvas editor open): shows the blue bubble in the bottom-left,
 * positioned near the ReactFlow Controls (+, -, fit, lock buttons).
 * When unmounted (canvas editor closed): hides the bubble again.
 *
 * Uses direct `window.Intercom` API instead of the React wrapper's `update()`
 * to bypass the boot-guard timing issue (the React wrapper silently drops calls
 * if the widget script hasn't fully loaded yet). Polls every 500ms for up to 10s
 * to handle the async script loading.
 *
 * Z-index 1400 (set in App.jsx GlobalStyles) ensures it renders above canvas Portal (1300).
 */
import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    /** Injected by the Intercom widget script — absent until it loads. */
    Intercom?: (command: string, params?: Record<string, unknown>) => void;
  }
}

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;

export const IntercomLauncherToggle: React.FC = () => {
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!INTERCOM_APP_ID) return;

    const showLauncher = () => {
      if (typeof window.Intercom === 'function') {
        window.Intercom('update', {
          hide_default_launcher: false,
          alignment: 'left',
          horizontal_padding: 100,
          vertical_padding: 40,
        });
      }
    };

    // Try immediately, then retry every 500ms until widget loads (max 10s)
    showLauncher();
    const interval = setInterval(() => {
      if (!mountedRef.current) return;
      showLauncher();
    }, 500);
    const timeout = setTimeout(() => clearInterval(interval), 10000);

    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      clearTimeout(timeout);
      if (typeof window.Intercom === 'function') {
        window.Intercom('update', { hide_default_launcher: true });
      }
    };
  }, []);

  return null;
};

export default IntercomLauncherToggle;
